
import React, { useState } from "react";

import {
    Avatar,
    Typography,
    CircularProgress,
    Tooltip,
} from "@mui/material";

import {
    CameraAlt,
    PersonOutline,
    ChatOutlined,
} from "@mui/icons-material";

import Swal from "sweetalert2";
import api, { BASE_URL } from "../api/api";
import defaultAvatar from "../Dashboard/maleee.jpg";
import { Link } from "react-router-dom";

const ParentAndStudentProfile = ({
    user,
    refreshUser,
    tempImage,
    setTempImage,
    theme,
    darkMode,
}) => {
    const [overlay, setOverlay] = useState(false);

    const COLORS = {
        navy: "#031B35",
        deepNavy: "#021426",
        gold: "#F6C944",
        goldLight: "#FFE58A",
        white: "#FFFFFF",
        muted: "#B8C7D6",
    };

    const token = localStorage.getItem("access");

    const profileImage =
        tempImage ||
        (user?.profile_image
            ? `${BASE_URL}${user.profile_image}`
            : defaultAvatar);

    const displayName =
        user?.full_name ||
        user?.name ||
        [user?.first_name, user?.last_name]
            .filter(Boolean)
            .join(" ") ||
        user?.username ||
        "Parent";

    const userRole = user?.user_type
        ? String(user.user_type)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase())
        : "User";

    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            Swal.fire("Invalid File", "Please select a valid image.", "warning");
            e.target.value = "";
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            Swal.fire(
                "Image Too Large",
                "Please select an image smaller than 5MB.",
                "warning"
            );
            e.target.value = "";
            return;
        }

        const formData = new FormData();
        formData.append("profile_image", file);

        const previewUrl = URL.createObjectURL(file);

        setTempImage(previewUrl);
        setOverlay(true);

        try {
            await api.put("/user-profile/", formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            Swal.fire({
                icon: "success",
                title: "Profile Updated",
                text: "Your profile picture has been updated successfully.",
                confirmButtonColor: COLORS.navy,
                timer: 2000,
                showConfirmButton: false,
            });

            await refreshUser();

        } catch (error) {
            console.error("Image update error", error);

            setTempImage(null);

            Swal.fire({
                icon: "error",
                title: "Upload Failed",
                text:
                    error?.response?.data?.detail ||
                    "Could not update your profile image. Please try again.",
                confirmButtonColor: COLORS.navy,
            });

        } finally {
            setOverlay(false);
            e.target.value = "";
        }
    };

    return (
        <>
            <style>
                {`
                    .compact-profile {
                        width: 100%;
                        box-sizing: border-box;
                        padding: 11px 10px;
                        border-radius: 12px;
                        background: linear-gradient(
                            120deg,
                            #0A2C4D,
                            #062A4B,
                            #031B35
                        );
                        border: 1px solid rgba(246,201,68,.22);
                        position: relative;
                        overflow: hidden;
                    }

                    .compact-profile::before {
                        content: "";
                        position: absolute;
                        width: 100px;
                        height: 100px;
                        right: -55px;
                        top: -65px;
                        border-radius: 50%;
                        background: rgba(246,201,68,.12);
                        pointer-events: none;
                    }

                    .compact-avatar-wrap {
                        position: relative;
                        width: 49px;
                        height: 49px;
                        flex-shrink: 0;
                        border-radius: 50%;
                        padding: 2px;
                        background: linear-gradient(
                            135deg,
                            #C0750D,
                            #F6C944,
                            #FFE58A
                        );
                    }

                    .compact-camera {
                        position: absolute;
                        bottom: -2px;
                        right: -3px;
                        width: 19px;
                        height: 19px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-radius: 50%;
                        background: #F6C944;
                        border: 2px solid #062A4B;
                        cursor: pointer;
                        transition: .2s ease;
                    }

                    .compact-camera:hover {
                        transform: scale(1.12);
                        background: #FFE58A;
                    }

                    .compact-chat {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 6px;
                        width: 100%;
                        min-height: 30px;
                        margin-top: 10px;
                        border-radius: 7px;
                        background: linear-gradient(
                            110deg,
                            #F6C944,
                            #FFE58A
                        );
                        color: #031B35;
                        text-decoration: none;
                        font-size: 11px;
                        font-weight: 800;
                        transition: .2s ease;
                    }

                    .compact-chat:hover {
                        background: #FFE58A;
                        color: #031B35;
                        transform: translateY(-1px);
                    }

                    .compact-profile-name {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .compact-camera,
                        .compact-chat {
                            transition: none;
                        }
                    }
                `}
            </style>

            <div className="compact-profile">

                {/* Horizontal profile section */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        minWidth: 0,
                    }}
                >
                    {/* Profile picture */}
                    <div className="compact-avatar-wrap">
                        <Avatar
                            src={profileImage}
                            alt={displayName}
                            sx={{
                                width: "100%",
                                height: "100%",
                                border: "2px solid #062A4B",
                                boxSizing: "border-box",
                                opacity: overlay ? 0.4 : 1,
                            }}
                        />

                        {overlay && (
                            <CircularProgress
                                size={23}
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-11.5px",
                                    marginLeft: "-11.5px",
                                    color: COLORS.gold,
                                }}
                            />
                        )}

                        <Tooltip title="Change profile picture" arrow>
                            <label
                                htmlFor="parent-profile-upload"
                                className="compact-camera"
                                style={{
                                    cursor: overlay ? "not-allowed" : "pointer",
                                }}
                            >
                                <CameraAlt
                                    sx={{
                                        fontSize: 11,
                                        color: COLORS.navy,
                                    }}
                                />
                            </label>
                        </Tooltip>

                        <input
                            type="file"
                            accept="image/*"
                            id="parent-profile-upload"
                            disabled={overlay}
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                        />
                    </div>

                    {/* User information */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            className="compact-profile-name"
                            title={displayName}
                            sx={{
                                color: COLORS.white,
                                fontSize: 12,
                                fontWeight: 800,
                                lineHeight: 1.4,
                            }}
                        >
                            {displayName}
                        </Typography>

                        <Typography
                            title={user?.email || ""}
                            sx={{
                                color: COLORS.muted,
                                fontSize: 10,
                                mt: 0.3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {user?.email || "No email available"}
                        </Typography>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                marginTop: 5,
                            }}
                        >
                            <PersonOutline
                                sx={{
                                    fontSize: 12,
                                    color: COLORS.gold,
                                }}
                            />

                            <Typography
                                sx={{
                                    color: COLORS.goldLight,
                                    fontSize: 9,
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                }}
                            >
                                {userRole}
                            </Typography>
                        </div>
                    </div>
                </div>

                {/* Compact Chats link */}
                <Link to="/chatApp" className="compact-chat">
                    <ChatOutlined sx={{ fontSize: 15 }} />
                    Open Chats
                </Link>
            </div>
        </>
    );
};

export default ParentAndStudentProfile;
